import { supabase } from './supabase';
import { StaticPage } from '../types';

/**
 * Fetch all pages from page_master table
 */
export async function fetchAllPages(): Promise<{ success: boolean; data?: StaticPage[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('page_master')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Supabase fetch pages error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data as StaticPage[] };
  } catch (error) {
    console.error('Fetch pages error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Fetch a single page by slug
 */
export async function fetchPageBySlug(slug: string): Promise<{ success: boolean; data?: StaticPage; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('page_master')
      .select('*')
      .eq('slug', slug)
      .eq('is_published', true)
      .single();

    if (error) {
      console.error('Supabase fetch page by slug error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data as StaticPage };
  } catch (error) {
    console.error('Fetch page by slug error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Fetch a single page by ID
 */
export async function fetchPageById(id: string): Promise<{ success: boolean; data?: StaticPage; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('page_master')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Supabase fetch page by ID error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data as StaticPage };
  } catch (error) {
    console.error('Fetch page by ID error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Create a new page
 */
export async function createPage(page: Omit<StaticPage, 'id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; data?: StaticPage; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('page_master')
      .insert([{
        title: page.title,
        slug: page.slug,
        content: page.content,
        is_published: page.is_published,
      }])
      .select()
      .single();

    if (error) {
      console.error('Supabase create page error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data as StaticPage };
  } catch (error) {
    console.error('Create page error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Update an existing page
 */
export async function updatePage(id: string, page: Partial<StaticPage>): Promise<{ success: boolean; data?: StaticPage; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('page_master')
      .update({
        title: page.title,
        slug: page.slug,
        content: page.content,
        is_published: page.is_published,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Supabase update page error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data as StaticPage };
  } catch (error) {
    console.error('Update page error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Delete a page
 */
export async function deletePage(id: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('page_master')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Supabase delete page error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete page error:', error);
    return { success: false, error: String(error) };
  }
}